/**
 * This is the COMMUNITY NEWS PAGE
 *
 * Page for specifically getting the "Community News" page
 * This uses the PageCommunityNews template and gets the "news" page at the Community level.
 * - Community News pages live under the path
 *   - /community/[__community__]/news-and-events/news/[__news__]
 */
import React from 'react';

import { GetStaticPaths, GetStaticProps } from 'next';
import { apolloClient, setApolloHeaders } from '@src/services/apollo';
import {
  CommunityBasePageQuery,
  GlobalSettingsQuery,
  NationalBasePageQuery,
  PageCommunityNewsPathQuery,
  PageCommunityNewsPathsQuery,
  PageCommunityNewsQuery
} from '@src/graphql/gql-types';
import {
  communityBasePageQuery,
  globalSettingsQuery,
  nationalBasePageQuery,
  pageCommunityNewsPathQuery,
  pageCommunityNewsPathsQuery,
  pageCommunityNewsQuery
} from '@src/graphql/queries';
import { flattenPagePaths, getSlugsFromArray } from '@src/utils/url';
import PageCommunityNewsTemplate from '@src/templates/PageCommunityNews';
import { CommunityNewsPageProps } from '@src/types/page';
import { getCommunityDetailSegmentFromFlattenPagePaths } from '@src/utils/helpers';
import {
  getValidPathsOnly,
  validateUrlSlugPathToBuildPath
} from '@src/utils/build/paths';
import { hashObjectToString } from '@src/utils/build/hash';
import { base64EncodeHero } from '@src/utils/build/base64EncodeHero';
import { ContentfulPageHyperlinkFragments } from '@src/types/contentful';

const CommunityNewsPage: React.FC<CommunityNewsPageProps> = (props) => (
  <PageCommunityNewsTemplate {...props} />
);

export default CommunityNewsPage;

/**
 * Create the paths params and cache the needed data to be passed to GetStaticProps
 * @param pages
 * @returns
 */
const createPathParams = (pages: any[]): any[] => {
  return pages
    .filter((page) => !!page?.slug)
    .map((page) => {
      /**
       * Get an array of the page paths (This flattens the nested parent path into a usable array) */
      const arrayOfPages = flattenPagePaths(page);

      /**
       * Now extract the slug names in to an array : ['page-a','page-b','page-c'] */
      const slugPathArray = getSlugsFromArray(arrayOfPages);

      /**
       * Get the first slug in the array which is the "community" slug. News has a required field community in contentful.*/
      const communitySlug = slugPathArray[0];

      /**
       * __Params to use for hash (Must match the same params passed in nextJS (in paths below))__ */
      const path = {
        params: {
          slug_community_news: page?.slug ?? '',
          slug_community_detail: communitySlug ?? ''
        }
      };

      return path;
    });
};

/**
 * Build static paths
 * - Based on the GraphQL query `pageCommunityNewsPaths.query` will return __Community News__ pages and also get the __community__ the news belongs to.
 * - Please confirm with checking in the GraphQL query for what is returned.
 * - Community News pages live under the path
 *   - /community/[__community__]/news-and-events/news/[__news__]
 *
 * __NOTE__: Community News pages do NOT have a unique slug, so when retrieving a news page we MUST use the contentful sys.id
 *  - Hence we store the page sys.id in a cache file for retrieval in getStaticProps
 * @returns path params
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const pageQueryResponse =
    await apolloClient.query<PageCommunityNewsPathsQuery>({
      query: pageCommunityNewsPathsQuery,
      variables: { preview: false }
    });

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  const pages =
    pageQueryResponse.data?.pageCommunityNewsCollection?.items || [];

  const paths = createPathParams(pages);
  const validPaths = getValidPathsOnly(paths);

  // Set fallback to 'blocking' so that if the page does not exist, nextJS will SSR the page (build the page server side)
  // We use this as community news can be published and be returned in the news search/listing results before the static page is built/deployed.
  // When this happens, if fallback is false, an error will occur. Using fallback: 'blocking' generates the page on demand and solves the above edge case.
  return {
    paths: validPaths,
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  /**
   * NOTE:
   * Hash the params to get the lookup ID to find the correct page. This is a server side function only.
   * This is created and applied to the page ('pageCommunityNews' content-type) through the webhook 'entryAssignTagToCommunity'
   * __IF THE HASH IS NOT CREATED AND APPLIED THEN THE PAGE WILL NOT BE FOUND__
   */
  const nextJsParamsHashId = params ? hashObjectToString(params) : 'no-hash';

  if (preview) {
    setApolloHeaders(process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_TOKEN);
  }

  // START: === Use the params hash to lookup/get the correct page
  const pageMetaData = await apolloClient.query<PageCommunityNewsPathQuery>({
    query: pageCommunityNewsPathQuery,
    variables: { nextJsParamsHashId: nextJsParamsHashId, preview: preview }
  });

  if (pageMetaData?.errors) {
    console.error(pageMetaData.errors);
  }

  // We need the sys.id, navigation_id and community_id for the queries below
  const pageExtraData = pageMetaData.data?.pagePath?.items?.[0];

  /**
   * Get an array of the page paths (This flattens the nested parent path into a usable array) */
  const arrayOfPagesPaths = flattenPagePaths(pageExtraData);
  // Get Community Details Segment , which will have reference to the Navigation to use
  const communityDetailSegments =
    getCommunityDetailSegmentFromFlattenPagePaths(arrayOfPagesPaths);

  const lookupData = {
    id: pageExtraData?.sys.id ?? '',
    navigation_id: communityDetailSegments?.navigation?.sys?.id || '',
    community_id: communityDetailSegments?.sys?.id || ''
  };
  // END ===

  const [
    globalSettingsQueryResponse,
    nationalBasePageQueryResponse,
    communityBasePageQueryResponse,
    pageQueryResponse
  ] = await Promise.all([
    apolloClient.query<GlobalSettingsQuery>({
      query: globalSettingsQuery,
      variables: {
        preview: preview
      }
    }),

    apolloClient.query<NationalBasePageQuery>({
      query: nationalBasePageQuery,
      variables: {
        preview: preview
      }
    }),

    apolloClient.query<CommunityBasePageQuery>({
      query: communityBasePageQuery,
      variables: {
        id: lookupData?.navigation_id,
        community_id: lookupData?.community_id,
        preview: preview
      }
    }),

    apolloClient.query<PageCommunityNewsQuery>({
      query: pageCommunityNewsQuery,
      variables: { id: lookupData?.id, preview: preview }
    })
  ]);

  if (globalSettingsQueryResponse?.errors) {
    console.error(globalSettingsQueryResponse.errors);
  }
  if (nationalBasePageQueryResponse?.errors) {
    console.error(nationalBasePageQueryResponse.errors);
  }

  if (communityBasePageQueryResponse?.errors) {
    console.error(communityBasePageQueryResponse.errors);
  }
  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  const pagePayload = await base64EncodeHero(pageQueryResponse?.data);

  const pagePathData = pagePayload?.pagePath
    ?.items?.[0] as ContentfulPageHyperlinkFragments;

  const paramPageSlugs = Object.values(params as Record<string, string>);

  const isValidSlugPath = validateUrlSlugPathToBuildPath(
    paramPageSlugs,
    pagePathData
  );

  if (!isValidSlugPath) {
    return { notFound: true };
  }

  const templatePropsData: CommunityNewsPageProps = {
    globalSettingsPayload: globalSettingsQueryResponse?.data,
    nationalBasePagePayload: nationalBasePageQueryResponse?.data,
    communityBasePagePayload: communityBasePageQueryResponse?.data,
    pagePayload: pagePayload as PageCommunityNewsQuery
  };

  return {
    props: {
      ...templatePropsData
    }
  };
};
